import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  X,
  Pencil,
  Trash2,
  Eye,
  ChevronRight,
  Calendar,
  Home,
  FileText,
  Link as LucideLink,
} from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';

/**
 * BECOME A MEMBER ADMIN PAGE
 * Main admin panel for managing news posts
 * Features:
 * - Add/Edit/Delete posts
 * - Post button management
 * - Content viewing via button click
 */
function BecomeAMember() {
  const navigate = useNavigate();
  
  // Color constant for consistent styling
  const emerald600 = "#059669";
  
  // API URLs - all using member app
  const API_BASE_URL = "https://backend.dlapakistan.com/api/member/";

  // Refs for Jodit Editor
  const editorRef = useRef(null);          // For post description editor
  
  /**
   * JODIT EDITOR CONFIGURATION
   * Memoized to prevent unnecessary re-renders
   */
  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 250,
    theme: "default",
    toolbarButtonSize: "medium",
    // Remove unnecessary buttons
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    // Define available buttons in toolbar
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#ffffff',
      color: '#374151',
    },
    uploader: {
      insertImageAsBase64URI: true  // Embed images as base64
    },
    placeholder: 'Write description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
    defaultMode: '1',
    iframe: false,
    showPlaceholder: true,
    controls: {
      font: {
        list: {
          'Arial, Helvetica, sans-serif': 'Arial',
          'Georgia, serif': 'Georgia',
          'Impact, Charcoal, sans-serif': 'Impact',
          'Tahoma, Geneva, sans-serif': 'Tahoma',
          'Times New Roman, Times, serif': 'Times New Roman',
          'Verdana, Geneva, sans-serif': 'Verdana',
          'Roboto, sans-serif': 'Roboto',
          'Open Sans, sans-serif': 'Open Sans'
        }
      },
      fontSize: {
        list: ['8', '9', '10', '11', '12', '14', '16', '18', '24', '30', '36', '48', '60', '72', '96']
      }
    },
    colorPickerDefaultTab: 'background',
    editorClassName: 'custom-jodit-editor',
    useSearch: false,
    spellcheck: false,
    allowResizeX: false,
    allowResizeY: false,
  }), []);

  // ============ STATE VARIABLES ============

  // Posts data state
  const [posts, setPosts] = useState([]);          // All news posts
  const [isLoading, setIsLoading] = useState(true); // Loading state

  // Content data state (for checking if button has content)
  const [contents, setContents] = useState([]);    // All added contents
  const [postWithContent, setPostWithContent] = useState({}); // Map: button -> content
  
  // Modal visibility states
  const [showPostModal, setShowPostModal] = useState(false);     // Post add/edit modal
  const [showDetailModal, setShowDetailModal] = useState(false); // Post detail modal
  const [selectedPost, setSelectedPost] = useState(null);        // Currently selected post for details

  // Post form state
  const [postForm, setPostForm] = useState({
    title: "",          // Post title
    description: "",    // Post description (HTML)
    image: null,        // Post image file
    buttonEnabled: false, // Whether button is enabled
    buttonName: "",     // Button text if enabled
  });

  // Additional state variables
  const [editingPost, setEditingPost] = useState(null);          // Post being edited (null for new)
  const [saving, setSaving] = useState(false);                   // Post save loading state
  const [formErrors, setFormErrors] = useState({ title: false, description: false }); // Post form errors
  const [imagePreview, setImagePreview] = useState(null);        // Image preview URL

  // ============ LIFECYCLE & DATA LOADING ============

  /**
   * Load initial data when component mounts
   */
  useEffect(() => {
    loadData();
    loadContents();
  }, []);

  /**
   * LOAD POSTS FROM API
   * Fetches all news posts from backend
   */
  const loadData = async () => {
    setIsLoading(true);
    try {
      const postsResponse = await fetch(`${API_BASE_URL}posts/`);
      if (!postsResponse.ok) throw new Error('Failed to fetch posts');
      
      const postsData = await postsResponse.json();
      setPosts(postsData);
    } catch (err) {
      console.error("Error loading data:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to load posts. Please check if server is running.",
        icon: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * LOAD CONTENTS FROM API
   * Fetches all content linked to buttons (for checking content existence)
   * Uses: GET /api/member/contents/
   */
  const loadContents = async () => {
    try {
      const contentsResponse = await fetch(`${API_BASE_URL}contents/`);
      
      if (contentsResponse.ok) {
        const contentsData = await contentsResponse.json();
        setContents(contentsData);
        
        // Create mapping of button names to their content
        const contentMap = {};
        contentsData.forEach(content => {
          contentMap[content.button] = content;
        });
        setPostWithContent(contentMap);
      }
    } catch (err) {
      console.error("Error loading contents:", err);
    }
  };

  // ============ HELPER FUNCTIONS ============

  /**
   * GET IMAGE URL
   * Returns proper image URL for a post
   * Handles different image formats and fallbacks
   * @param {Object} post - Post object
   * @returns {string} - Image URL
   */
  const getImageUrl = (post) => {
    if (!post) return "https://via.placeholder.com/300x150/059669/ffffff?text=No+Image";
    
    // Priority 1: Check image_url field (from new backend)
    if (post.image_url) {
      return post.image_url;
    }
    
    // Priority 2: Check image field
    if (post.image) {
      // If it's already full URL
      if (post.image.startsWith('http')) {
        return post.image;
      }
      // If it's relative path
      if (post.image.startsWith('/media/')) {
        return `https://backend.dlapakistan.com${post.image}`;
      }
      // If it's just filename
      return `https://backend.dlapakistan.com/media/posts/${post.image}`;
    }
    
    // No image available - return placeholder
    return "https://via.placeholder.com/300x150/059669/ffffff?text=No+Image";
  };

  /**
   * FORMAT DATE
   * Converts date string to readable format
   * @param {string} dateString - Date string from API
   * @returns {string} - Formatted date
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Invalid date" : 
      date.toLocaleDateString("en-US", { 
        year: "numeric", 
        month: "long", 
        day: "numeric" 
      });
  };

  /**
   * GENERATE SLUG
   * Creates URL-friendly slug from button name
   * @param {string} buttonName - Button text
   * @returns {string} - Generated slug
   */
  const generateSlug = (buttonName) => {
    if (!buttonName) return "";
    return buttonName.toLowerCase()
      .replace(/[^\w\s-]/g, "")  // Remove special characters
      .replace(/\s+/g, "-")      // Replace spaces with hyphens
      .replace(/--+/g, "-")      // Replace multiple hyphens with single
      .trim();                   // Remove extra spaces
  };

  // ============ EVENT HANDLERS ============

  /**
   * HANDLE POST EDITOR CHANGE
   * Updates post description when Jodit editor changes
   * @param {string} newContent - New HTML content from editor
   */
  const handleEditorChange = (newContent) => {
    setPostForm(prev => ({ ...prev, description: newContent }));
    if (newContent.trim()) setFormErrors(prev => ({ ...prev, description: false }));
  };

  /**
   * HANDLE POST FORM INPUT CHANGE
   * Updates post form state for all input types
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === "file") {
      // Handle file upload
      const file = files[0];
      if (file) {
        setPostForm(prev => ({ ...prev, [name]: file }));
        // Create preview for image
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else if (type === "checkbox") {
      // Handle checkbox
      setPostForm(prev => ({ ...prev, [name]: checked }));
    } else {
      // Handle text inputs
      setPostForm(prev => ({ ...prev, [name]: value }));
      if (name === "title" && value.trim()) setFormErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  // ============ POST CRUD OPERATIONS ============

  /**
   * ADD NEW POST
   * Validates and submits new post to API
   * Uses: POST /api/member/posts/
   */
  const handleAddPost = async () => {
    // Validate required fields
    let hasErrors = false;
    const newErrors = { title: false, description: false };

    if (!postForm.title.trim()) { newErrors.title = true; hasErrors = true; }
    if (!postForm.description.trim()) { newErrors.description = true; hasErrors = true; }

    setFormErrors(newErrors);
    if (hasErrors) {
      Swal.fire({
        title: "Required Fields Missing",
        html: `<div class="text-left"><p class="mb-2">Please fill in:</p><ul class="list-disc pl-5">
          ${newErrors.title ? "<li><strong>Post Title</strong></li>" : ""}
          ${newErrors.description ? "<li><strong>Description</strong></li>" : ""}</ul></div>`,
        icon: "warning",
        confirmButtonColor: emerald600,
      });
      return;
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("title", postForm.title);
    formData.append("description", postForm.description);
    if (postForm.image) formData.append("image", postForm.image);
    if (postForm.buttonEnabled && postForm.buttonName.trim()) {
      formData.append("button", postForm.buttonName);
      formData.append("slug", generateSlug(postForm.buttonName));
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}posts/`, { 
        method: "POST", 
        body: formData 
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const newPost = await response.json();
      setPosts(prev => [newPost, ...prev]); // Add new post to beginning
      closePostModal();
      
      Swal.fire({
        title: "Success!",
        text: "Post added successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Failed to add post:", err);
      Swal.fire({ 
        title: "Error", 
        text: `Failed to add post: ${err.message}`, 
        icon: "error" 
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * EDIT POST
   * Sets up form with post data for editing
   * @param {Object} post - Post object to edit
   */
  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title || "",
      description: post.description || "",
      image: null,
      buttonEnabled: !!post.button,
      buttonName: post.button || "",
    });
    setImagePreview(post.image ? getImageUrl(post) : null);
    setShowPostModal(true);
  };

  /**
   * UPDATE POST
   * Saves edited post to API
   * Uses: PATCH /api/member/posts/{id}/
   */
  const handleUpdatePost = async () => {
    // Validate required fields
    let hasErrors = false;
    const newErrors = { title: false, description: false };

    if (!postForm.title.trim()) { newErrors.title = true; hasErrors = true; }
    if (!postForm.description.trim()) { newErrors.description = true; hasErrors = true; }

    setFormErrors(newErrors);
    if (hasErrors) {
      Swal.fire({
        title: "Required Fields Missing",
        html: `<div class="text-left"><p class="mb-2">Please fill in:</p><ul class="list-disc pl-5">
          ${newErrors.title ? "<li><strong>Post Title</strong></li>" : ""}
          ${newErrors.description ? "<li><strong>Description</strong></li>" : ""}</ul></div>`,
        icon: "warning",
        confirmButtonColor: emerald600,
      });
      return;
    }

    // Create FormData for update
    const formData = new FormData();
    formData.append("title", postForm.title);
    formData.append("description", postForm.description);
    if (postForm.image) formData.append("image", postForm.image);
    if (postForm.buttonEnabled && postForm.buttonName.trim()) {
      formData.append("button", postForm.buttonName);
      formData.append("slug", generateSlug(postForm.buttonName));
    } else {
      formData.append("button", "");
      formData.append("slug", "");
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}posts/${editingPost.id}/`, {
        method: "PATCH",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Update failed: ${errorText}`);
      }

      const updatedPost = await response.json();
      setPosts(prev => prev.map(p => (p.id === updatedPost.id ? updatedPost : p)));
      closePostModal();
      
      Swal.fire({
        title: "Updated!",
        text: "Post updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire({ 
        title: "Error", 
        text: `Failed to update post: ${err.message}`, 
        icon: "error" 
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * DELETE POST
   * Confirms and deletes a post
   * Uses: DELETE /api/member/posts/{id}/
   * @param {number} id - Post ID to delete
   */
  const handleDeletePost = (id) => {
    Swal.fire({
      title: "Delete Post?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${API_BASE_URL}posts/${id}/`, { method: "DELETE" })
          .then((res) => {
            if (!res.ok) throw new Error("Failed to delete");
            setPosts(prev => prev.filter(post => post.id !== id));
            Swal.fire("Deleted!", "Post has been deleted.", "success");
          })
          .catch((err) => {
            console.error(err);
            Swal.fire("Error", "Failed to delete post", "error");
          });
      }
    });
  };

  /**
   * HANDLE BUTTON CLICK
   * Always navigate to content page
   * @param {string} buttonText - Button text that was clicked
   */
  const handleButtonClick = (buttonText) => {
    if (!buttonText) return;
    
    const content = postWithContent[buttonText];
    if (content) {
      // If content exists, navigate to content page
      navigate(`/About-Dla/BecomeMember/content/${content.slug}`);
    } else {
      // If content doesn't exist, navigate to create content page
      // We'll use the slug from button text
      const slug = generateSlug(buttonText);
      navigate(`/About-Dla/BecomeMember/content/new?button=${encodeURIComponent(buttonText)}&slug=${slug}`);
    }
  };

  /**
   * VIEW POST DETAILS
   * Opens modal with full post details
   * @param {Object} post - Post to view details of
   */
  const handleViewDetails = (post) => {
    setSelectedPost(post);
    setShowDetailModal(true);
  };

  /**
   * CHECK IF POST HAS CONTENT
   * Determines if content exists for a button
   * @param {string} buttonText - Button text to check
   * @returns {boolean} - True if content exists
   */
  const hasContent = (buttonText) => {
    return buttonText && postWithContent[buttonText];
  };

  // ============ MODAL MANAGEMENT ============

  /**
   * CLOSE POST MODAL
   * Resets post form and closes modal
   */
  const closePostModal = () => {
    setPostForm({ 
      title: "", 
      description: "", 
      image: null, 
      buttonEnabled: false, 
      buttonName: "" 
    });
    setEditingPost(null);
    setFormErrors({ title: false, description: false });
    setImagePreview(null);
    setShowPostModal(false);
  };

  /**
   * CLOSE DETAIL MODAL
   * Closes post detail modal
   */
  const closeDetailModal = () => {
    setSelectedPost(null);
    setShowDetailModal(false);
  };

  /**
   * COPY SLUG TO CLIPBOARD
   * Copies slug text for sharing/linking
   * @param {string} slug - Slug to copy
   */
  const copySlug = (slug) => {
    if (!slug) return;
    navigator.clipboard.writeText(slug).then(() => {
      Swal.fire({
        title: "Copied!",
        text: "Slug copied to clipboard",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  // ============ RENDER LOGIC ============

  // Show loading spinner while data loads
  if (isLoading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading posts...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full bg-gray-50">
      {/* HEADER SECTION */}
      <div className="px-8 py-6 bg-white border-b">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">News Posts Management</h1>
            <p className="text-gray-600 mt-1">Manage news posts and content</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/dental/news")}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors gap-2"
            >
              <Home className="w-5 h-5" /> Back to News
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        <div className="space-y-10">
          {/* POSTS SECTION */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <FileText className="text-emerald-600" size={24} />
                <h3 className="text-2xl font-bold text-gray-900">News Posts</h3>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                  {posts.length} {posts.length === 1 ? "Post" : "Posts"}
                </span>
              </div>
              <div className="flex gap-3">
                {/* ADD POST BUTTON - Only post management */}
                <button
                  onClick={() => setShowPostModal(true)}
                  className="flex items-center px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-md gap-2"
                >
                  <Plus size={18} /> Add Post
                </button>
              </div>
            </div>

            {/* POSTS GRID */}
            {posts.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-xl bg-transparent">
                <FileText className="mx-auto text-gray-300 mb-4" size={56} />
                <p className="text-gray-400 text-lg">No posts added yet.</p>
                <button
                  onClick={() => setShowPostModal(true)}
                  className="mt-6 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                >
                  Add First Post
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {posts.map((post) => {
                  const hasContentForThisPost = hasContent(post.button);
                  return (
                    <div key={post.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all duration-300 bg-white group flex flex-col h-[300px]">
                      {/* POST IMAGE */}
                      <div className="h-28 relative bg-gray-100 overflow-hidden flex-shrink-0">
                        <img
                          src={getImageUrl(post)}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/300x150/059669/ffffff?text=Error"; }}
                        />
                        {/* ACTION BUTTONS (shown on hover) */}
                        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button onClick={() => handleViewDetails(post)} className="p-1 bg-white/90 backdrop-blur-sm rounded-md shadow hover:bg-white transition-colors" title="View details">
                            <Eye size={12} className="text-blue-600" />
                          </button>
                          <button onClick={() => handleEditPost(post)} className="p-1 bg-white/90 backdrop-blur-sm rounded-md shadow hover:bg-white transition-colors" title="Edit">
                            <Pencil size={12} className="text-emerald-600" />
                          </button>
                          <button onClick={() => handleDeletePost(post.id)} className="p-1 bg-white/90 backdrop-blur-sm rounded-md shadow hover:bg-white transition-colors" title="Delete">
                            <Trash2 size={12} className="text-red-600" />
                          </button>
                        </div>
                      </div>

                      {/* POST CONTENT */}
                      <div className="p-3 flex flex-col flex-grow">
                        <h4 className="font-semibold text-sm text-gray-900 mb-1 line-clamp-2">{post.title}</h4>
                        <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                          <Calendar size={10} />
                          <span className="text-[10px]">{formatDate(post.created_at)}</span>
                        </div>
                        {post.slug && (
                          <div className="mb-1 flex items-center gap-1">
                            <LucideLink size={9} className="text-gray-400" />
                            <span className="text-[9px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded truncate">
                              {post.slug}
                            </span>
                            <button onClick={() => copySlug(post.slug)} className="ml-auto text-[9px] text-emerald-600 hover:text-emerald-700" title="Copy slug">
                              Copy
                            </button>
                          </div>
                        )}
                        <div className="mb-2 flex-grow">
                          <div className="text-gray-600 text-xs line-clamp-2">
                            {post.description.replace(/<[^>]*>/g, "").substring(0, 70)}
                            {post.description.length > 70 ? "..." : ""}
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-auto">
                          {/* READ MORE BUTTON */}
                          <button onClick={() => handleViewDetails(post)} className="px-2 py-1 border border-emerald-600 text-emerald-600 rounded text-xs font-medium hover:bg-emerald-50 transition-all flex items-center gap-1">
                            Read More <ChevronRight size={10} />
                          </button>
                          
                          {/* ACTION BUTTON (if post has button) - ALWAYS ENABLED */}
                          {post.button && (
                            <button 
                              onClick={() => handleButtonClick(post.button)}
                              className="px-2 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 cursor-pointer transition-colors"
                              title={hasContentForThisPost ? `View content for ${post.button}` : `Add content for ${post.button}`}
                            >
                              {post.button}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* POST ADD/EDIT MODAL */}
      {showPostModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closePostModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto animate-modalIn border border-gray-100" onClick={(e) => e.stopPropagation()}>
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{editingPost ? "Edit Post" : "Add New Post"}</h2>
                <button onClick={closePostModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <div className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* IMAGE UPLOAD SECTION */}
                  <div className="md:col-span-1">
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:bg-gray-50 transition-colors h-full">
                      <input type="file" accept="image/*" onChange={handleInputChange} className="hidden" id="post-image" name="image" />
                      <label htmlFor="post-image" className="cursor-pointer block h-full">
                        {imagePreview ? (
                          <div>
                            <img src={imagePreview} alt="preview" className="w-full h-48 object-cover rounded-xl mx-auto mb-4 shadow-md" />
                            <span className="text-sm text-emerald-600 mt-3 inline-block font-medium">Change image</span>
                          </div>
                        ) : editingPost && editingPost.image ? (
                          <div>
                            <img src={getImageUrl(editingPost)} alt="current" className="w-full h-48 object-cover rounded-xl mx-auto mb-4 shadow-md" />
                            <span className="text-sm text-emerald-600 mt-3 inline-block font-medium">Change image</span>
                          </div>
                        ) : (
                          <div className="py-8 h-full flex flex-col justify-center">
                            <div className="mx-auto text-gray-400 mb-3" style={{ fontSize: "48px" }}>📷</div>
                            <p className="text-gray-600 text-lg">Upload post image</p>
                            <p className="text-sm text-gray-400 mt-2">JPG / PNG • Recommended: 300x150px</p>
                            <span className="text-sm text-emerald-600 mt-4 inline-block font-medium">Select image (optional)</span>
                          </div>
                        )}
                      </label>
                      {postForm.image && <p className="text-xs text-gray-500 mt-2">Selected: {postForm.image.name} ({(postForm.image.size / 1024).toFixed(1)} KB)</p>}
                    </div>
                  </div>

                  {/* FORM FIELDS */}
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Post Title <span className="text-red-500">*</span></label>
                      <input name="title" value={postForm.title} onChange={handleInputChange} className={`w-full px-5 py-3 border rounded-2xl outline-none focus:ring-2 transition-all ${formErrors.title ? "border-red-500 bg-red-50" : "border-gray-200 bg-gray-50"}`} style={{ "--tw-ring-color": formErrors.title ? "#ef4444" : emerald600 }} placeholder="Enter post title" />
                      {formErrors.title && <p className="mt-1 text-sm text-red-600">Title is required</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Description <span className="text-red-500">*</span></label>
                      <div className={`rounded-2xl overflow-hidden border ${formErrors.description ? "border-red-500" : "border-gray-200"}`}>
                        <div className="jodit-editor-container">
                          <JoditEditor ref={editorRef} value={postForm.description} config={editorConfig} onChange={handleEditorChange} onBlur={(newContent) => { if (newContent !== postForm.description) setPostForm(prev => ({...prev, description: newContent})); }} />
                        </div>
                      </div>
                      {formErrors.description && <p className="mt-1 text-sm text-red-600">Description is required</p>}
                    </div>
                  </div>
                </div>

                {/* BUTTON OPTIONS SECTION */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <input type="checkbox" id="buttonEnabled" name="buttonEnabled" checked={postForm.buttonEnabled} onChange={handleInputChange} className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                    <label htmlFor="buttonEnabled" className="font-medium text-gray-700">Add Action Button (Button name will become slug)</label>
                  </div>
                  {postForm.buttonEnabled && (
                    <div className="space-y-4 pl-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                        <input type="text" name="buttonName" value={postForm.buttonName} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" placeholder="e.g., Read More, Download, etc." />
                      </div>
                      {postForm.buttonName.trim() && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <LucideLink size={16} className="text-emerald-600" />
                            <span className="font-medium text-emerald-700">Auto-generated Slug:</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-white px-3 py-1.5 rounded border border-emerald-200 font-mono flex-grow">/news/{generateSlug(postForm.buttonName)}</code>
                            <button type="button" onClick={() => copySlug(generateSlug(postForm.buttonName))} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors">Copy</button>
                          </div>
                          <p className="text-xs text-emerald-600 mt-2">Slug will be automatically generated from button text</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex gap-3 pt-6">
                  <button type="button" onClick={closePostModal} className="flex-1 py-3 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-all text-lg font-medium">Cancel</button>
                  <button type="button" onClick={editingPost ? handleUpdatePost : handleAddPost} disabled={saving} style={{ backgroundColor: emerald600 }} className="flex-1 py-3 text-white rounded-2xl font-bold hover:brightness-110 transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {saving ? "Saving..." : editingPost ? "Update Post" : "Add Post"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* POST DETAIL MODAL */}
      {showDetailModal && selectedPost && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeDetailModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto pointer-events-auto animate-modalIn border border-gray-100" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white/90 backdrop-blur-md px-8 py-5 border-b flex justify-between items-center z-10">
                <h2 className="text-2xl font-bold text-gray-800">Post Details</h2>
                <button onClick={closeDetailModal} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X size={24} /></button>
              </div>
              <div className="p-8 md:p-12">
                {selectedPost.image && (
                  <div className="mb-8 rounded-xl overflow-hidden">
                    <img src={getImageUrl(selectedPost)} alt={selectedPost.title} className="w-full h-96 object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/800x400/059669/ffffff?text=Image+Not+Found"; }} />
                  </div>
                )}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar size={18} />
                    <span className="font-medium">{formatDate(selectedPost.created_at)}</span>
                  </div>
                  {selectedPost.slug && (
                    <div className="flex items-center gap-2">
                      <LucideLink size={18} className="text-gray-400" />
                      <div className="flex-grow">
                        <span className="text-sm text-gray-600 font-medium">Slug (from button):</span>
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono">/news/{selectedPost.slug}</code>
                          <button onClick={() => copySlug(selectedPost.slug)} className="text-emerald-600 hover:text-emerald-700 text-sm">Copy</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-6">{selectedPost.title}</h1>
                <div className="prose prose-lg max-w-none">
                  <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: selectedPost.description }} />
                </div>
                {selectedPost.button && (
                  <div className="mt-8 pt-6 border-t">
                    <button className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors">{selectedPost.button}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* CUSTOM STYLES */}
      <style jsx>{`
        /* Animation keyframes */
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
        .animate-modalIn { animation: modalIn 0.4s ease-out forwards; }
        
        /* Utility classes */
        .line-clamp-2 { 
          display: -webkit-box; 
          -webkit-line-clamp: 2; 
          -webkit-box-orient: vertical; 
          overflow: hidden; 
        }
        .truncate { 
          overflow: hidden; 
          text-overflow: ellipsis; 
          white-space: nowrap; 
        }

        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #ffffff !important;
          border-radius: 16px !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #ffffff !important;
          min-height: 200px;
          border-radius: 0 0 16px 16px !important;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 16px 16px 0 0 !important;
          padding: 8px 4px !important;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #ffffff !important;
          color: #374151 !important;
          padding: 20px !important;
          min-height: 150px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
}

export default BecomeAMember;